import {
  makeObservable,
  observable,
  computed,
  action,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { UploadedFileModelData } from "./types";
import { UploadedFileObservable } from "./UploadedFileObservable";
import { SyncUpdateValue } from "@/store/sync/types";
import { Maybe } from "@/domains/common/types";
import { Dexie, liveQuery, Subscription } from "dexie";
import { searchUploadedFiles } from "@/store/uploaded-files/search-uploaded-files";
import { uuidModule } from "@/modules/uuid";
import { BatchCreateUploadedFileOperation } from "@/store/sync/operations/uploaded-file/BatchCreateUploadedFileOperation";
import { FileUploadResult } from "@/store/upload-progress/AppStoreUploadProgressStore";

export class AppStoreUploadedFileStore extends BaseSyncModelStore<
  UploadedFileObservable,
  UploadedFileModelData
> {
  liveQuerySubscription: Maybe<Subscription>;
  uploadedFiles: UploadedFileObservable[] = [];
  private initialLoaded: boolean = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.UploadedFile, ...injectedDeps });
    makeObservable<
      this,
      "subscribeLiveQuery" | "unsubscribeLiveQuery" | "setIsReady" | "initialLoaded"
    >(this, {
      searchUploadedFiles: true,
      createSyncModel: false,

      uploadedFiles: observable,
      liveQuerySubscription: observable,
      subscribeLiveQuery: action,
      unsubscribeLiveQuery: action,

      initialLoaded: observable,
      setIsReady: action,
      isReady: computed,
      createUploadedFiles: action,
    });

    onBecomeObserved(this, "uploadedFiles", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "uploadedFiles", () => this.unsubscribeLiveQuery());
    onBecomeObserved(this, "isReady", () => this.subscribeLiveQuery());
  }

  get isReady() {
    return this.initialLoaded;
  }

  createSyncModel(data: SyncUpdateValue<UploadedFileModelData>): UploadedFileObservable {
    return new UploadedFileObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  public searchUploadedFiles = async (batchId?: string): Promise<UploadedFileObservable[]> => {
    const ids = await Dexie.waitFor(searchUploadedFiles({ store: this.store, batchId }));
    return this.getMany(ids);
  };

  /**
   * Creates multiple uploaded files at once.
   *
   * @param batchId The ID of the batch these files belong to
   * @param files Array of file details containing normalizedFileName, fileMimeType, fileSizeBytes, and uploadedFileBlobName
   * @returns Array of IDs of the newly created files
   */
  public async createUploadedFiles({
    batchId,
    uploadedFileResults,
  }: {
    batchId: string;
    uploadedFileResults: FileUploadResult[];
  }): Promise<string[]> {
    const syncOperationId = uuidModule.generate();
    const fileIds = uploadedFileResults.map(() => uuidModule.generate());
    const payload = {
      id: syncOperationId,
      uploaded_file_batch_id: batchId,
      uploaded_files: await Promise.all(
        uploadedFileResults.map(async (file, index) => ({
          id: fileIds[index],
          uploaded_file_batch_id: batchId,
          uploaded_file_blob_name: file.uploadedFileBlobName,
          mime_type: file.info.fileMimeType,
          normalized_filename: file.info.normalizedFileName,
          file_size_bytes: file.info.fileSizeBytes,
          original_filename: file.fileItem.name,
          original_filepath: file.fileItem.fullPath,
          // Get timestamp from file and convert to seconds
          modified_at_timestamp: await file.fileItem
            .getFile()
            .then((file: File) => Math.floor(file.lastModified / 1000)),
        }))
      ),
    };

    await new BatchCreateUploadedFileOperation({
      store: this.store,
      payload,
    }).execute();

    return fileIds;
  }

  private subscribeLiveQuery() {
    this.setIsReady(false);
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(() => {
      return this.searchUploadedFiles();
    }).subscribe({
      next: items => {
        runInAction(() => {
          this.uploadedFiles = items;
          this.setIsReady(true);
        });
      },
    });
  }

  private unsubscribeLiveQuery() {
    this.setIsReady(false);
    this.liveQuerySubscription?.unsubscribe();
  }

  private setIsReady(value: boolean) {
    this.initialLoaded = value;
  }
}
